/* eslint-disable no-promise-executor-return */
// TODO remove linting exception after OTT-25002

/* eslint-disable no-restricted-imports */
import { path } from 'ramda';
import { appendScript, QueryParamsStore } from '@funtech-int/yango-shared';
import { MetrikaConfig } from '@yandex-int/any.common/ts/metrika/metrikaConfig';

import { Logger } from '@kinopoisk-int/logger';

interface YandexMetrika {
    getCounters: () => Counter[];
}

interface YandexMetrika2 {
    // eslint-disable-next-line @typescript-eslint/no-misused-new
    new (data: MetrikaConfig): YandexMetrika2;

    params: (
        visitParams: VisitParams,
        callback?: () => void,
        ctx?: Record<any, any>
    ) => YandexMetrika2;
    reachGoal: (
        goalName: string,
        params?: Record<string, any>,
        callback?: () => void,
        ctx?: Record<any, any>
    ) => YandexMetrika2;
}

interface Ym {
    (counter: number, method: Methods, params?: string | Partial<CounterParams>): void;
    (
        counter: number,
        method: Methods.PARAMS,
        params?: string | Partial<CounterParams> | VisitParams,
        callback?: () => void,
        ctx?: Record<any, any>
    ): void;
    (
        counter: number,
        method: Methods.REACH_GOAL,
        target: string,
        params?: Partial<CounterParams>,
        callback?: () => void,
        ctx?: Record<any, any>
    ): void;
}

export interface CounterConfig {
    id: number;
    params: CounterParams;
}

enum Methods {
    INIT = 'init',
    DESTRUCT = 'destruct',
    PARAMS = 'params',
    REACH_GOAL = 'reachGoal',
}

interface AdditionalCounterParams {
    appName: string;
    appVersion: string;
    isAuthorized: boolean;
    yaGDPRLang?: string;
}

interface CounterParams {
    clickmap: boolean;
    trackLinks?: boolean;
    accurateTrackBounce?: boolean;
    webvisor: boolean;
    extraOriginParameters?: {
        hashOrderId: string;
    };
    params: AdditionalCounterParams;
    /** Page URL for init (passed to ym(counterId, 'init', params)) */
    url?: string;
}

interface Counter {
    id: number;
    params: CounterParams;
}

interface VisitParam {
    [key: string]: any;
}

// https://yandex.ru/support/metrica/objects/params-method.html
// https://yandex.ru/support/metrica/data/visit-params_data.html
type VisitParams = VisitParam | VisitParam[];

export interface InitMetrikaParams {
    scriptSrc: string;
    counter?: CounterConfig;
    trackedQueryParamsStore?: QueryParamsStore;
    logger?: Logger;
}

const LOADING_RETRIES_DELAY = 1000;
const LOAD_RETRIES = 3;
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'];

const buildUrlWithParams = (
    params: Record<string, string> | undefined,
    baseUrl = window.location.href
): string => {
    const url = new URL(baseUrl);

    if (params) {
        Object.entries(params).forEach(([key, value]) => {
            url.searchParams.set(key, value);
        });
    }

    return url.toString();
};

const getWindowCounterName = (counter: number): string => `yaCounter${counter}`;

/** Loading metrika script to page */
const loadMetrika = async (scriptUrl: string, retriesCount = 0): Promise<void> => {
    try {
        if (document.querySelector(`script[src="${scriptUrl}"]`)) {
            if (window.Ya?.Metrika2) {
                return;
            }

            await new Promise<void>((resolve, reject) => {
                let asyncLoadCallback = window.yandex_metrika_callbacks2;

                if (!Array.isArray(asyncLoadCallback)) {
                    asyncLoadCallback = [];

                    window.yandex_metrika_callbacks2 = asyncLoadCallback;
                }

                asyncLoadCallback.push(() => {
                    if (window.Ya?.Metrika2) {
                        resolve();
                    } else {
                        reject();
                    }
                });
            });

            return;
        }
        await appendScript(scriptUrl, document.body);
    } catch (error) {
        if (retriesCount > 0) {
            // eslint-disable-next-line no-promise-executor-return
            await new Promise((resolve) => setTimeout(resolve, LOADING_RETRIES_DELAY));

            return loadMetrika(scriptUrl, retriesCount - 1);
        }

        throw error;
    }
};

/** Get metrika library from global scope */
const getMetrikaLib = (): {
    ym?: Ym;
    metrika?: YandexMetrika;
    Metrika2?: YandexMetrika2;
} => ({
    ym: path<Ym>(['ym'], window),
    metrika: path<YandexMetrika>(['Ya', '_metrika'], window),
    Metrika2: path<YandexMetrika2>(['Ya', 'Metrika2'], window),
});

const initMetrikaCounter = (counter: CounterConfig, pageUrl: string) => {
    const { metrika, ym, Metrika2 } = getMetrikaLib();

    const hasMetrikaInterface = Boolean(ym || Metrika2);
    const hasMetrikaInternals = Boolean(metrika);

    if (!hasMetrikaInterface || !hasMetrikaInternals) {
        return;
    }

    const { id, params } = counter;

    const counters: Counter[] = (metrika as YandexMetrika).getCounters();
    const counterIds = counters.map((el) => el.id);

    // We can add counter only if it doesn't exist
    if (counterIds.includes(id)) {
        return;
    }

    if (ym) {
        ym(id, Methods.INIT, { ...params, url: pageUrl });
    } else if (Metrika2) {
        window[getWindowCounterName(id)] = new Metrika2({
            ...params,
            id,
        });
    }
};

/** Initializing metrika */
export const initMetrika = async ({
    counter,
    logger,
    trackedQueryParamsStore,
    scriptSrc,
}: InitMetrikaParams) => {
    try {
        await loadMetrika(scriptSrc, LOAD_RETRIES);
    } catch (error) {
        logger?.warn(
            {
                error,
            },
            'Metrika load error'
        );
    }

    if (!counter) {
        return;
    }

    const pageUrl = buildUrlWithParams(trackedQueryParamsStore?.getParams(UTM_PARAMS));

    initMetrikaCounter(counter, pageUrl);
};
